/**
 * T01 — 대기배출시설 및 방지시설 운영기록부
 * ---------------------------------------------------------------------------
 * 구역 이름과 칸 이름은 「대기환경보전법 시행규칙」 별지 제7호서식을 따른다.
 *
 *   1. 배출구별 주요 배출시설 및 방지시설 가동(조업)시간
 *   2. 방지시설 운영사항
 *        가. 방지시설 운전사항
 *        나. 방지시설 보수사항   ← 보수 내역은 편집기 아래쪽에 있다
 *
 * 입력 화면과 인쇄물의 말이 다르면 사용자는 어느 칸이 서식의 어디로 가는지
 * 매번 짐작해야 한다. 같은 말을 쓴다.
 *
 * 행은 시설 마스터에서 자동으로 채워진다. 사용자는 값만 적는다.
 * 원본 WinForm 은 매일 시설명을 손으로 적게 했고, 그것이 오타와 집계 오류의
 * 원인이었다.
 *
 * 직접 추가한 행
 *   마스터에 없는 시설을 그날만 적어야 할 때가 있다(임시 설비, 미등록 시설).
 *   마스터에서 온 행은 시설명을 고칠 수 없게 두고, 직접 추가한 행만 열어
 *   준다. 마스터 시설의 이름을 일지에서 고치면 같은 시설이 날마다 다른
 *   이름으로 남는다.
 *
 * 열 너비
 *   원료명·약품명·사용량은 실제로 길게 적는 칸이고, 비고는 거의 비어 있다.
 *   종이 서식의 칸 비율이 아니라 실제 입력량에 맞춘다.
 *
 * 가동 시간
 *   시작·종료를 고르면 자동으로 계산해 채운다. 직접 고친 값이 있어도
 *   시간대를 다시 고르면 계산값으로 돌아간다 — 시간대와 맞지 않는 가동
 *   시간이 남아 있는 쪽이 더 나쁘다.
 */

import { Alert, Button, Card, Table, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { AirOutletRow, AirPreventionRow } from '@eco/shared';
import {
  DEFAULT_UPTIME_END,
  DEFAULT_UPTIME_START,
  operatingHoursBetween,
} from '@eco/shared';
import { NumberCell, TextCell, TimeCell, appendRow, patchRow, removeRow } from './rows';

const EMPTY_OUTLET: AirOutletRow = {
  facilityId: null,
  outletNo: '',
  facilityName: '',
  uptimeStart: DEFAULT_UPTIME_START,
  uptimeEnd: DEFAULT_UPTIME_END,
  operatingHours: operatingHoursBetween(DEFAULT_UPTIME_START, DEFAULT_UPTIME_END),
  materialName: null,
  materialUsage: null,
  materialUnit: null,
  note: null,
  sortOrder: 0,
};

const EMPTY_PREVENTION: AirPreventionRow = {
  facilityId: null,
  outletNo: null,
  facilityName: '',
  installLocation: null,
  powerConsumption: null,
  treatmentCapacity: null,
  powerCapacity: null,
  pollutant: null,
  concentration: null,
  efficiency: null,
  drugName: null,
  drugUse: null,
  drugUnit: null,
  note: null,
  sortOrder: 0,
};

export function AirForm({
  outlets,
  preventions,
  locked,
  onOutlets,
  onPreventions,
}: {
  outlets: AirOutletRow[];
  preventions: AirPreventionRow[];
  locked: boolean;
  onOutlets: (rows: AirOutletRow[]) => void;
  onPreventions: (rows: AirPreventionRow[]) => void;
}) {
  const navigate = useNavigate();

  /** 시간대를 고치면 가동 시간을 같이 맞춘다. */
  const setUptime = (index: number, part: Partial<AirOutletRow>) => {
    const merged = { ...outlets[index]!, ...part };
    onOutlets(
      patchRow(outlets, index, {
        ...part,
        operatingHours: operatingHoursBetween(merged.uptimeStart, merged.uptimeEnd),
      }),
    );
  };

  return (
    <>
      {/*
        시설이 어디서 오는지 알려 준다.

        표가 비었을 때만 알리면 늦다. 행을 손으로 하나 추가해 쓰기 시작한
        사람은 "원래 이렇게 매일 적는 것" 으로 알고 계속 적는다. 그래서
        비어 있든 아니든 항상 둔다.
      */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          <>
            배출시설·오염방지시설은 <b>사업장·시설 메뉴 → 「사업장 시설관리」</b> 에서
            등록합니다.
          </>
        }
        description="거기에 등록해 두면 일지를 열 때 아래 표가 자동으로 채워집니다. 등록하지 않으면 매일 손으로 적어야 하고, 시설별 집계도 맞지 않습니다."
        action={
          <Button size="small" onClick={() => navigate('/sites')}>
            사업장·시설로
          </Button>
        }
      />

      <Card
        title="1. 배출구별 주요 배출시설 및 방지시설 가동(조업)시간"
        size="small"
        extra={
          !locked && (
            <Button size="small" onClick={() => onOutlets(appendRow(outlets, EMPTY_OUTLET))}>
              행 추가
            </Button>
          )
        }
      >
        <Table<AirOutletRow>
          className="eco-grid"
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          scroll={{ x: 1260 }}
          dataSource={outlets}
          locale={{
            emptyText: '등록된 대기배출시설이 없습니다. 사업장·시설에서 먼저 등록하세요.',
          }}
          columns={[
            {
              title: '배출구',
              width: 80,
              fixed: 'left',
              render: (_, row, i) =>
                // 마스터에서 온 행은 시설이 정한 값이다. 직접 추가한 행만 연다.
                row.facilityId ? (
                  row.outletNo || '-'
                ) : (
                  <TextCell
                    value={row.outletNo}
                    disabled={locked}
                    maxLength={20}
                    onChange={(v) => onOutlets(patchRow(outlets, i, { outletNo: v ?? '' }))}
                  />
                ),
            },
            {
              title: '배출시설',
              width: 200,
              fixed: 'left',
              render: (_, row, i) =>
                row.facilityId ? (
                  row.facilityName
                ) : (
                  <TextCell
                    value={row.facilityName}
                    disabled={locked}
                    maxLength={200}
                    placeholder="배출시설명"
                    onChange={(v) => onOutlets(patchRow(outlets, i, { facilityName: v ?? '' }))}
                  />
                ),
            },
            {
              title: '가동(조업) 시작',
              width: 105,
              render: (_, row, i) => (
                <TimeCell
                  value={row.uptimeStart}
                  disabled={locked}
                  onChange={(v) => setUptime(i, { uptimeStart: v })}
                />
              ),
            },
            {
              title: '가동(조업) 종료',
              width: 105,
              render: (_, row, i) => (
                <TimeCell
                  value={row.uptimeEnd}
                  disabled={locked}
                  onChange={(v) => setUptime(i, { uptimeEnd: v })}
                />
              ),
            },
            {
              title: '계(h)',
              width: 95,
              render: (_, row, i) => (
                <NumberCell
                  value={row.operatingHours}
                  disabled={locked}
                  min={0}
                  max={24}
                  step={0.5}
                  // 시작·종료에서 자동으로 채운다. 예외적인 경우만 직접 고친다.
                  placeholder="자동"
                  onChange={(v) => onOutlets(patchRow(outlets, i, { operatingHours: v }))}
                />
              ),
            },
            {
              title: '원료(연료)명',
              width: 260,
              render: (_, row, i) => (
                <TextCell
                  value={row.materialName}
                  disabled={locked}
                  maxLength={100}
                  onChange={(v) => onOutlets(patchRow(outlets, i, { materialName: v }))}
                />
              ),
            },
            {
              title: '사용량',
              width: 110,
              render: (_, row, i) => (
                <NumberCell
                  value={row.materialUsage}
                  disabled={locked}
                  min={0}
                  onChange={(v) => onOutlets(patchRow(outlets, i, { materialUsage: v }))}
                />
              ),
            },
            {
              title: '단위',
              width: 80,
              render: (_, row, i) => (
                <TextCell
                  value={row.materialUnit}
                  disabled={locked}
                  maxLength={20}
                  onChange={(v) => onOutlets(patchRow(outlets, i, { materialUnit: v }))}
                />
              ),
            },
            {
              title: '비고',
              width: 120,
              render: (_, row, i) => (
                <TextCell
                  value={row.note}
                  disabled={locked}
                  maxLength={500}
                  onChange={(v) => onOutlets(patchRow(outlets, i, { note: v }))}
                />
              ),
            },
            {
              title: '',
              width: 60,
              fixed: 'right',
              render: (_, __, i) =>
                !locked && (
                  <Button
                    size="small"
                    type="link"
                    danger
                    onClick={() => onOutlets(removeRow(outlets, i))}
                  >
                    삭제
                  </Button>
                ),
            },
          ]}
        />
        {/* 서식에 인쇄되어 있는 안내다. 입력할 때 보이지 않으면 아무도 비고에
            정상 여부를 적지 않는다. */}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          * 비고란은 정상 여부를 기재합니다.
        </Typography.Text>
      </Card>

      <Card
        // 서식은 절 제목 아래 줄에 소제목을 들여 쓴다. 한 줄로 붙이면
        // "가" 가 "2" 의 하위라는 것이 드러나지 않는다.
        title={
          <>
            <div>2. 방지시설 운영사항</div>
            <div style={{ marginLeft: 16, fontWeight: 400 }}>가. 방지시설 운전사항</div>
          </>
        }
        size="small"
        extra={
          !locked && (
            <Button
              size="small"
              onClick={() => onPreventions(appendRow(preventions, EMPTY_PREVENTION))}
            >
              행 추가
            </Button>
          )
        }
      >
        <Table<AirPreventionRow>
          className="eco-grid"
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          scroll={{ x: 1690 }}
          dataSource={preventions}
          locale={{ emptyText: '등록된 대기방지시설이 없습니다.' }}
          columns={[
            {
              title: '배출구',
              width: 80,
              fixed: 'left',
              render: (_, row, i) =>
                row.facilityId ? (
                  row.outletNo || '-'
                ) : (
                  <TextCell
                    value={row.outletNo}
                    disabled={locked}
                    maxLength={20}
                    onChange={(v) => onPreventions(patchRow(preventions, i, { outletNo: v }))}
                  />
                ),
            },
            {
              title: '방지시설명',
              width: 200,
              fixed: 'left',
              render: (_, row, i) =>
                row.facilityId ? (
                  row.facilityName
                ) : (
                  <TextCell
                    value={row.facilityName}
                    disabled={locked}
                    maxLength={200}
                    placeholder="방지시설명"
                    onChange={(v) =>
                      onPreventions(patchRow(preventions, i, { facilityName: v ?? '' }))
                    }
                  />
                ),
            },
            {
              // 서식의 칸이다. 시설 마스터 값으로 채워 오지만 그날 옮겨 단
              // 이동식 설비처럼 달라질 수 있어 고칠 수 있게 둔다.
              title: '설치위치',
              width: 160,
              render: (_, row, i) => (
                <TextCell
                  value={row.installLocation}
                  disabled={locked}
                  maxLength={200}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { installLocation: v }))}
                />
              ),
            },
            {
              title: '전력사용량(㎾/h)',
              width: 130,
              render: (_, row, i) => (
                <NumberCell
                  value={row.powerConsumption}
                  disabled={locked}
                  min={0}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { powerConsumption: v }))}
                />
              ),
            },
            {
              title: '처리용량(㎥/min)',
              width: 130,
              render: (_, row, i) => (
                <NumberCell
                  value={row.treatmentCapacity}
                  disabled={locked}
                  min={0}
                  onChange={(v) =>
                    onPreventions(patchRow(preventions, i, { treatmentCapacity: v }))
                  }
                />
              ),
            },
            {
              // 시설 마스터 값으로 채워 오지만 고칠 수 있어야 한다. 한 시설이
              // "먼지, 황산화물" 처럼 여러 물질을 처리하고, 그날 무엇을
              // 처리했는지는 조업 내용에 따라 달라진다.
              title: '처리오염물질',
              width: 200,
              render: (_, row, i) => (
                <TextCell
                  value={row.pollutant}
                  disabled={locked}
                  maxLength={100}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { pollutant: v }))}
                />
              ),
            },
            {
              title: '처리농도(ppm 또는 ㎎/S㎥)',
              width: 130,
              render: (_, row, i) => (
                <NumberCell
                  value={row.concentration}
                  disabled={locked}
                  min={0}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { concentration: v }))}
                />
              ),
            },
            {
              title: '처리효율(%)',
              width: 105,
              render: (_, row, i) => (
                <NumberCell
                  value={row.efficiency}
                  disabled={locked}
                  min={0}
                  max={100}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { efficiency: v }))}
                />
              ),
            },
            {
              title: '사용약품 — 약품명',
              width: 240,
              render: (_, row, i) => (
                <TextCell
                  value={row.drugName}
                  disabled={locked}
                  maxLength={100}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { drugName: v }))}
                />
              ),
            },
            {
              title: '사용약품 — 사용량',
              width: 220,
              render: (_, row, i) => (
                <NumberCell
                  value={row.drugUse}
                  disabled={locked}
                  min={0}
                  onChange={(v) => onPreventions(patchRow(preventions, i, { drugUse: v }))}
                />
              ),
            },
            // 단위·비고는 두지 않는다. 서식의 「가. 방지시설 운전사항」 에
            // 없는 칸이라 적어도 인쇄되지 않는다.
            {
              title: '',
              width: 60,
              fixed: 'right',
              render: (_, __, i) =>
                !locked && (
                  <Button
                    size="small"
                    type="link"
                    danger
                    onClick={() => onPreventions(removeRow(preventions, i))}
                  >
                    삭제
                  </Button>
                ),
            },
          ]}
        />
      </Card>
    </>
  );
}
